import { useState } from 'react'
import { faCheck } from '@fortawesome/pro-duotone-svg-icons'

import Button from '../button/Button'
import Icon from '../icon/Icon'
import TextField from '../text-field/TextField'
import {
  RowIcon,
  RowSubtitle,
  RowText,
  RowTitle,
  StatusPill,
} from '../verifications-manager/styled'
import { resolveStatusDisplay } from '../verifications-manager/helpers'
import {
  confirmContactVerification,
  startContactVerification,
} from '../../_data/verifications'
import {
  resolveConfirmMessage,
  resolveOpenLabel,
  resolveSendMessage,
} from './helpers'
import { ContactVerificationRowProps, ContactVerificationState } from './types'
import {
  ExpandedArea,
  FieldSlot,
  HeaderLeft,
  HeaderRight,
  Message,
  RowHeader,
  RowShell,
} from './styled'

const INITIAL_STATE: ContactVerificationState = {
  stage: 'idle',
  destination: '',
  code: '',
  message: '',
  isBusy: false,
}

/**
 * One free-tier verification row: prove an email address or a phone number by
 * receiving a code at it and typing the code back.
 *
 * The same component drives both channels because the only thing that differs is
 * the wording and the keyboard hint, and keeping one row means the two can never
 * drift into behaving differently.
 *
 * @param {ContactVerificationRowProps} props
 * @returns {JSX.Element}
 */
const ContactVerificationRow = ({
  quickPassUserId,
  channel,
  title,
  subtitle,
  icon,
  verification,
  defaultDestination,
  onVerified,
}: ContactVerificationRowProps): JSX.Element => {
  const [state, setState] = useState<ContactVerificationState>(INITIAL_STATE)

  const status = verification?.status ?? 'unverified'
  const statusDisplay = resolveStatusDisplay(status)
  const isVerified = status === 'verified'

  const openRow = (): void => {
    setState({
      ...INITIAL_STATE,
      stage: 'destination',
      destination: defaultDestination,
    })
  }

  const closeRow = (): void => {
    setState(INITIAL_STATE)
  }

  const handleSend = async (): Promise<void> => {
    setState(current => ({ ...current, isBusy: true, message: '' }))

    const result = await startContactVerification(
      quickPassUserId,
      channel,
      state.destination,
    )

    if (result === null) {
      setState(current => ({
        ...current,
        isBusy: false,
        message: 'We could not send that code just now. Please try again.',
      }))
      return
    }

    setState(current => ({
      ...current,
      isBusy: false,
      code: '',
      stage: result.outcome === 'sent' ? 'code' : current.stage,
      message: resolveSendMessage(
        result.outcome,
        channel,
        result.maskedDestination,
        result.retryAfterSeconds,
      ),
    }))
  }

  const handleConfirm = async (): Promise<void> => {
    setState(current => ({ ...current, isBusy: true, message: '' }))

    const result = await confirmContactVerification(
      quickPassUserId,
      channel,
      state.code,
    )

    if (result === null) {
      setState(current => ({
        ...current,
        isBusy: false,
        message: 'We could not check that code just now. Please try again.',
      }))
      return
    }

    if (result.outcome === 'verified') {
      setState(INITIAL_STATE)
      onVerified()
      return
    }

    setState(current => ({
      ...current,
      isBusy: false,
      message: resolveConfirmMessage(result.outcome, result.attemptsRemaining),
    }))
  }

  return (
    <RowShell>
      <RowHeader>
        <HeaderLeft>
          <RowIcon>
            <Icon icon={icon} label={title} />
          </RowIcon>
          <RowText>
            <RowTitle>{title}</RowTitle>
            <RowSubtitle>
              {isVerified && verification !== null
                ? verification.displayValue
                : subtitle}
            </RowSubtitle>
          </RowText>
        </HeaderLeft>
        <HeaderRight>
          <StatusPill tone={statusDisplay.tone}>
            {isVerified ? <Icon icon={faCheck} /> : null}
            {statusDisplay.label}
          </StatusPill>
          <Button
            type="button"
            variant={isVerified ? 'secondary' : 'primary'}
            size="small"
            onClick={() => {
              if (state.stage === 'idle') {
                openRow()
                return
              }

              closeRow()
            }}
          >
            {state.stage === 'idle' ? resolveOpenLabel(isVerified) : 'Cancel'}
          </Button>
        </HeaderRight>
      </RowHeader>

      {state.stage === 'destination' && (
        <ExpandedArea>
          <FieldSlot>
            <TextField
              label={channel === 'email' ? 'Email address' : 'Mobile number'}
              value={state.destination}
              inputMode={channel === 'email' ? 'email' : 'tel'}
              autoComplete={channel === 'email' ? 'email' : 'tel'}
              disabled={state.isBusy}
              onChange={changeEvent => {
                const { value } = changeEvent.target
                setState(current => ({ ...current, destination: value }))
              }}
            />
          </FieldSlot>
          <Button
            type="button"
            size="small"
            isLoading={state.isBusy}
            onClick={() => {
              void handleSend()
            }}
          >
            Send code
          </Button>
        </ExpandedArea>
      )}

      {state.stage === 'code' && (
        <ExpandedArea>
          <FieldSlot>
            <TextField
              label="Confirmation code"
              value={state.code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              disabled={state.isBusy}
              onChange={changeEvent => {
                const { value } = changeEvent.target
                setState(current => ({ ...current, code: value }))
              }}
            />
          </FieldSlot>
          <Button
            type="button"
            size="small"
            isLoading={state.isBusy}
            onClick={() => {
              void handleConfirm()
            }}
          >
            Confirm
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="small"
            disabled={state.isBusy}
            onClick={() => {
              void handleSend()
            }}
          >
            Resend
          </Button>
        </ExpandedArea>
      )}

      {state.message !== '' && <Message>{state.message}</Message>}
    </RowShell>
  )
}

export default ContactVerificationRow
